import { useEffect, useState } from 'react';
import MainLayout          from './layout/MainLayout';
import LandingPage         from './pages/LandingPage';
import SignupChoicePage    from './pages/SignupChoicePage';
import IndividualSetupPage from './pages/IndividualSetupPage';
import OrganizationSetupPage from './pages/OrganizationSetupPage';
import LoginPage           from './pages/LoginPage';
import GlobalConfirmationModal from './components/GlobalConfirmationModal';
import useAuthStore        from './store/authStore';
import useOrganizationStore from './store/organizationStore';
import useDealStore        from './store/dealStore';
import './styles/global.css';

/*
  Screen flow (unauthenticated):
  ┌─────────────┐
  │   Landing   │  → Sign Up  → signup-choice
  │             │  → Sign In  → login
  └─────────────┘

  ┌──────────────────┐
  │  signup-choice   │  → Organisation → org-setup
  │                  │  → Individual   → individual-setup
  │                  │  ← Back         → landing
  └──────────────────┘

  org-setup     → on complete/skip → login
  individual-setup → on sign-in link → login
                  → on back          → signup-choice
                  → on complete      → (auth store sets isAuthenticated → MainLayout)

  Authenticated → MainLayout (dashboard)
*/

function App() {
  const { isAuthenticated, checkAuth } = useAuthStore();
  const { isLoaded, loadProfile }      = useOrganizationStore();
  const { loadDealState }              = useDealStore();
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  // Possible values: 'landing' | 'signup-choice' | 'org-setup' | 'individual-setup' | 'login'
  const [screen, setScreen] = useState('landing');

  useEffect(() => {
    checkAuth();
    loadProfile();
    loadDealState();
    setIsCheckingAuth(false);
  }, [checkAuth, loadProfile, loadDealState, isAuthenticated]);

  // ── Loading ──────────────────────────────────────────
  if (isCheckingAuth || !isLoaded) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
        <p>Loading AcquireOS AI™…</p>
      </div>
    );
  }

  const renderScreen = () => {
    if (isAuthenticated) {
      return <MainLayout />;
    }

    switch (screen) {
      case 'signup-choice':
        return (
          <SignupChoicePage
            onOrgSetup={()   => setScreen('org-setup')}
            onIndividual={()  => setScreen('individual-setup')}
            onBack={()        => setScreen('landing')}
          />
        );

      case 'org-setup':
        return (
          <OrganizationSetupPage
            onComplete={() => setScreen('login')}
          />
        );

      case 'individual-setup':
        return (
          <IndividualSetupPage
            onSignIn={() => setScreen('login')}
            onBack={()   => setScreen('signup-choice')}
          />
        );

      case 'login':
        return (
          <LoginPage
            onBack={() => setScreen('landing')}
          />
        );

      case 'landing':
      default:
        return (
          <LandingPage
            onSignUp={() => setScreen('signup-choice')}
            onSignIn={() => setScreen('login')}
          />
        );
    }
  };

  return (
    <>
      {renderScreen()}
      <GlobalConfirmationModal />
    </>
  );
}

export default App;

